import { formFieldKeys } from '@messenger/consts'
import type { AnyNode, Element } from 'domhandler'

/**
 * Separator analysis for name-part spans (prefix/first/nick/middle/maiden/
 * last/suffix). Hidden name spans sit inline between visible ones; the
 * whitespace next to them is sometimes an orphan (line-edge spaces that shift
 * center-aligned text) and sometimes the only thing separating two visible
 * name parts. Removal logic must distinguish the two — removing a load-bearing
 * separator merges names ("Vera Bril" → "VeraBril").
 *
 * These helpers are pure analysis over the domhandler tree shared by cheerio;
 * callers perform the actual mutations.
 */

const HIDDEN_STYLE = /display:\s*none/i

export const namePartFieldKeys: readonly string[] = [
  formFieldKeys.prefix,
  formFieldKeys.firstName,
  formFieldKeys.nickName,
  formFieldKeys.middleName,
  formFieldKeys.maidenName,
  formFieldKeys.lastName,
  formFieldKeys.suffix,
]

const isTag = (node: AnyNode | null | undefined): node is Element =>
  !!node && node.type === 'tag'

const isHiddenTag = (node: AnyNode | null | undefined): boolean =>
  isTag(node) && HIDDEN_STYLE.test(node.attribs?.style || '')

const textOf = (node: AnyNode): string => {
  if (node.type === 'text') return (node as { data?: string }).data || ''
  if (isTag(node))
    return node.children.map((child) => textOf(child as AnyNode)).join('')
  return ''
}

/** Text the node contributes to rendering — display:none subtrees excluded. */
export const renderedTextOf = (node: AnyNode): string => {
  if (node.type === 'text') return (node as { data?: string }).data || ''
  if (!isTag(node) || isHiddenTag(node)) return ''
  return node.children.map((child) => renderedTextOf(child as AnyNode)).join('')
}

/**
 * Zero-width characters (ZWSP/ZWNJ/ZWJ/BOM) survive String.trim() but render
 * no ink — for separator decisions they must count as nothing at all.
 */
const ZERO_WIDTH = /\u200B|\u200C|\u200D|\uFEFF/g

export const inkOf = (text: string): string => text.replace(ZERO_WIDTH, '')

/**
 * Non-empty text that renders no ink \u2014 whitespace and/or zero-width
 * characters only. The shared gate for "may this text node be treated as a
 * removable whitespace separator": raw `.trim()` / `/^\s+$/` checks disagree
 * with rendering on zero-width characters (U+FEFF is ES whitespace, U+200B
 * is not), so removal sites must classify through the ink lens.
 */
export const isBlankInkText = (text: string): boolean =>
  text.length > 0 && inkOf(text).trim() === ''

const isWhitespaceText = (node: AnyNode): boolean => {
  if (node.type !== 'text') return false
  const ink = inkOf(textOf(node))
  return ink.trim() === '' && /\s/.test(ink)
}

/**
 * A styled <span> containing only whitespace — the Name Order plugin uses
 * these instead of text nodes because TinyMCE normalizes away bare text nodes
 * between mceNonEditable spans.
 */
const isSpaceSpanTag = (node: AnyNode): boolean => {
  if (!isTag(node)) return false
  if (node.attribs?.['data-case-detail']) return false
  const ink = inkOf(textOf(node))
  return ink.trim() === '' && ink.length > 0
}

/** Node currently rendering as a word gap. */
const isActiveSeparator = (node: AnyNode): boolean =>
  isWhitespaceText(node) || (isSpaceSpanTag(node) && !isHiddenTag(node))

export const isNamePartSpan = (node: AnyNode | null | undefined): boolean =>
  isTag(node) &&
  namePartFieldKeys.includes(node.attribs?.['data-case-detail'] || '')

type SideScan = { rendered: boolean; separators: number }

/**
 * Walk siblings from startIdx in `direction` until the first node that
 * renders visible content, counting active separators passed on the way.
 * Hidden elements and visible-but-blank spans (e.g. hair-space placeholders)
 * are skipped.
 */
const scanSide = (
  siblings: AnyNode[],
  startIdx: number,
  direction: 1 | -1,
): SideScan => {
  const scan: SideScan = { rendered: false, separators: 0 }
  for (let i = startIdx; i >= 0 && i < siblings.length; i += direction) {
    const node = siblings[i]
    if (isActiveSeparator(node)) {
      scan.separators += 1
      continue
    }
    const ink = inkOf(renderedTextOf(node))
    if (ink.trim() !== '') {
      scan.rendered = true
      // A separator can live at the terminating node's gap-facing edge —
      // template text ending in "&nbsp;", or a wrapper whose inner edge is
      // a space. It bridges the gap exactly like a sibling separator node.
      const gapEdge = direction === -1 ? ink[ink.length - 1] : ink[0]
      if (/\s/.test(gapEdge)) scan.separators += 1
      break
    }
  }
  return scan
}

const siblingsOf = (node: AnyNode): AnyNode[] | null => {
  const parent = node.parent
  if (!parent || !('children' in parent)) return null
  return (parent as Element).children as AnyNode[]
}

/**
 * May this separator (whitespace text node or visible space span) be removed
 * or hidden without merging two visible pieces of content?
 *
 * True when it is an edge orphan — no rendered content on at least one side,
 * the case the #2337 center-alignment fix targets — or when another separator
 * still bridges the same gap. False when it is the sole separator between two
 * visible pieces: removing it would glue them together.
 */
export const canRemoveSeparator = (
  separator: AnyNode | null | undefined,
): boolean => {
  if (!separator) return true
  const siblings = siblingsOf(separator)
  if (!siblings) return true
  const idx = siblings.indexOf(separator)
  if (idx === -1) return true
  const left = scanSide(siblings, idx - 1, -1)
  const right = scanSide(siblings, idx + 1, 1)
  if (!left.rendered || !right.rendered) return true
  return left.separators + right.separators > 0
}

/**
 * True when a hidden name-part span sits between rendered content on both
 * sides with no separator left anywhere in the gap — i.e. its neighbors are
 * glued together. This is the state earlier versions persisted by removing
 * the whitespace on both sides of hidden spans; callers repair it by
 * inserting a single space next to the span.
 *
 * The repair is a BARE TEXT-NODE space on purpose (a styled space span was
 * considered and rejected): a text node inherits the wrapper's font/size/
 * transform and wraps exactly like a naturally-typed name, while a heal-built
 * span would need guessed inline styles in arbitrary damaged content and
 * would make the editor and sanitize paths emit different DOM for the same
 * input. TinyMCE's normalization of text nodes between mceNonEditable spans
 * is not a durability risk: if the space is ever stripped, the next render
 * pass re-detects the glue and re-heals — worst case a transient re-heal,
 * never loss (proven live in name-parts-glue e2e Step 6).
 */
export const isGluedAroundHidden = (
  hiddenSpan: AnyNode | null | undefined,
): boolean => {
  if (!hiddenSpan || !isNamePartSpan(hiddenSpan) || !isHiddenTag(hiddenSpan))
    return false
  const siblings = siblingsOf(hiddenSpan)
  if (!siblings) return false
  const idx = siblings.indexOf(hiddenSpan)
  if (idx === -1) return false
  const left = scanSide(siblings, idx - 1, -1)
  const right = scanSide(siblings, idx + 1, 1)
  return (
    left.rendered && right.rendered && left.separators + right.separators === 0
  )
}
